import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  forwardRef,
  inject,
  signal,
} from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NodeViewComponent } from '@examdojo/core/tiptap';
import { finalize, fromEvent, takeUntil, tap } from 'rxjs';
import { StopPropagationDirective } from '@examdojo/core/stop-propagation';
import { TiptapNodeViewContentDirective } from 'ngx-tiptap';
import { v4 as uuid } from 'uuid';
import { ColumnsBlock, ContentBlockType } from '../../../model';
import { DragWithHandleOnlyDirective } from '../drag-with-handle-only.directive';
import { COLUMN_NODE_NAME } from './columns.model';

@Component({
  selector: 'dojo-columns',
  standalone: true,
  imports: [TiptapNodeViewContentDirective, StopPropagationDirective, MatTooltipModule],
  templateUrl: './columns.component.html',
  styleUrls: ['./columns.component.scss'],
  hostDirectives: [DragWithHandleOnlyDirective],
  providers: [
    {
      provide: NodeViewComponent,
      useExisting: forwardRef(() => ColumnsComponent),
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ColumnsComponent extends NodeViewComponent<ColumnsBlock> {
  constructor() {
    super();
    this.setupCssVariableEffect();
  }

  private readonly elementRef = inject(ElementRef);

  readonly isEditable = computed(() => this.editor().isEditable);
  readonly customCss = this.getProp('custom_css');
  readonly showBorders = this.getProp('show_borders');
  readonly columnWidths = this.getProp('column_widths');

  readonly columnCount = computed(() => this.node().childCount);
  readonly canAddColumn = computed(() => this.columnCount() < 3);
  readonly canRemoveColumn = computed(() => this.columnCount() > 2);
  readonly isTwoColumns = computed(() => this.columnCount() === 2);

  readonly effectiveWidths = computed(() => {
    const stored = this.columnWidths();
    const count = this.columnCount();
    if (stored && stored.length === count) {
      return stored;
    }
    return Array(count).fill(Math.round(100 / count));
  });

  private readonly resizingWidths = signal<number[] | null>(null);
  private resizeStartX = 0;
  private resizeStartLeftPercent = 50;

  readonly displayWidths = computed(() => this.resizingWidths() ?? this.effectiveWidths());

  readonly displayHandleLeftPercent = computed(() => {
    const widths = this.displayWidths();
    return widths.length >= 2 ? widths[0] : 50;
  });

  override getDefaults = (): ColumnsBlock => ({
    id: uuid(),
    type: ContentBlockType.Columns,
    columns: [],
  });

  toggleBorders(): void {
    this.updateProp('show_borders', !this.showBorders());
  }

  addColumn(): void {
    const editor = this.editor();
    const pos = this.getPos()();
    const node = this.node();

    if (node.childCount >= 3) {
      return;
    }

    const insertPos = pos + node.nodeSize - 1;
    editor
      .chain()
      .insertContentAt(insertPos, {
        type: COLUMN_NODE_NAME,
        content: [{ type: 'paragraph' }],
      })
      .run();

    if (this.columnWidths()) {
      this.resetColumnWidths();
    }
  }

  removeLastColumn(): void {
    const editor = this.editor();
    const pos = this.getPos()();
    const node = this.node();

    if (node.childCount <= 2) {
      return;
    }

    const lastChild = node.child(node.childCount - 1);
    const lastChildPos = pos + node.nodeSize - 1 - lastChild.nodeSize;
    const { tr } = editor.state;
    tr.delete(lastChildPos, lastChildPos + lastChild.nodeSize);
    editor.view.dispatch(tr);

    if (this.columnWidths()) {
      this.resetColumnWidths();
    }
  }

  onResizeStart(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.resizeStartX = event.clientX;
    this.resizeStartLeftPercent = this.effectiveWidths()[0];

    const mouseUp$ = fromEvent<MouseEvent>(document, 'mouseup');

    fromEvent<MouseEvent>(document, 'mousemove')
      .pipe(
        tap((moveEvent) => {
          const containerEl = this.elementRef.nativeElement.querySelector('.columns-block') as HTMLElement;
          if (!containerEl) {
            return;
          }
          const containerWidth = containerEl.getBoundingClientRect().width;
          const deltaX = moveEvent.clientX - this.resizeStartX;
          const deltaPercent = (deltaX / containerWidth) * 100;
          let newLeftPercent = this.resizeStartLeftPercent + deltaPercent;

          // Snap to nearest 10%
          newLeftPercent = Math.round(newLeftPercent / 10) * 10;
          // Clamp to 20-80
          newLeftPercent = Math.max(20, Math.min(80, newLeftPercent));

          this.resizingWidths.set([newLeftPercent, 100 - newLeftPercent]);
        }),
        finalize(() => {
          const widths = this.resizingWidths();
          if (widths) {
            if (widths[0] === 50 && widths[1] === 50) {
              this.resetColumnWidths();
            } else {
              this.updateProp('column_widths', widths);
            }
          }
          this.resizingWidths.set(null);
        }),
        takeUntil(mouseUp$),
      )
      .subscribe();
  }

  /** Reset column_widths to default. ProseMirror requires null (not undefined) to clear attributes. */
  private resetColumnWidths(): void {
    this.updateAttributes()({ column_widths: null });
  }

  private setupCssVariableEffect(): void {
    effect(() => {
      const el = this.elementRef.nativeElement.querySelector('.columns-block') as HTMLElement;
      if (!el) {
        return;
      }
      const widths = this.displayWidths();
      if (widths.length === 2) {
        el.style.setProperty('--col-1-flex', String(widths[0]));
        el.style.setProperty('--col-2-flex', String(widths[1]));
      } else {
        el.style.removeProperty('--col-1-flex');
        el.style.removeProperty('--col-2-flex');
      }
    });
  }
}
